import React, { useState } from 'react';

const FraudDetectionCard = ({ fraudAnalysis }) => {
  const [showDetails, setShowDetails] = useState(false);

  if (!fraudAnalysis || fraudAnalysis.error) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm dark:shadow-gray-900/50 p-6 mb-6 transition-colors duration-300">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-800 dark:text-white transition-colors duration-300">Fraud Detection Analysis</h3>
          <div className="px-3 py-1 rounded-full text-sm font-medium text-gray-600 dark:text-gray-300 bg-gray-50 dark:bg-gray-700 transition-colors duration-300">
            NOT AVAILABLE
          </div>
        </div>
        <div className="text-center text-gray-500 dark:text-gray-400 py-4 transition-colors duration-300">
          <i className="fa-solid fa-shield-halved text-4xl text-gray-300 dark:text-gray-600 mb-3"></i>
          <p className="text-sm">
            {fraudAnalysis?.error || 'Fraud analysis not available for this document'}
          </p>
        </div>
      </div>
    );
  }

  const getRiskColor = (level) => {
    switch(level) {
      case 'LOW': return 'text-green-600 dark:text-green-300 bg-green-50 dark:bg-green-900/30 border-green-200 dark:border-green-800';
      case 'MEDIUM': return 'text-yellow-600 dark:text-yellow-300 bg-yellow-50 dark:bg-yellow-900/30 border-yellow-200 dark:border-yellow-800';
      case 'HIGH': return 'text-red-600 dark:text-red-300 bg-red-50 dark:bg-red-900/30 border-red-200 dark:border-red-800';
      default: return 'text-gray-600 dark:text-gray-300 bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600';
    }
  };

  const getRiskIcon = (level) => {
    switch(level) {
      case 'LOW': return 'fa-shield-check text-green-600 dark:text-green-400';
      case 'MEDIUM': return 'fa-shield-halved text-yellow-600 dark:text-yellow-400';
      case 'HIGH': return 'fa-shield-xmark text-red-600 dark:text-red-400';
      default: return 'fa-shield text-gray-600 dark:text-gray-400';
    }
  };

  const getProgressBarColor = (score) => {
    if (score < 0.3) return 'bg-green-500';
    if (score < 0.6) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  const formatScore = (score) => {
    return (score * 100).toFixed(1);
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm dark:shadow-gray-900/50 border border-gray-200 dark:border-gray-700 p-6 mb-6 transition-colors duration-300">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <i className={`fa-solid ${getRiskIcon(fraudAnalysis.risk_level)} text-2xl mr-3 transition-colors duration-300`}></i>
          <h3 className="text-lg font-medium text-gray-800 dark:text-white transition-colors duration-300">Fraud Detection Analysis</h3>
        </div>
        <div className={`px-3 py-1 rounded-full text-sm font-bold border transition-colors duration-300 ${getRiskColor(fraudAnalysis.risk_level)}`}>
          {fraudAnalysis.risk_level} RISK
        </div>
      </div>
      
      {/* Main Fraud Score */}
      <div className="mb-6">
        <div className="flex justify-between items-center mb-2">
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300 transition-colors duration-300">Fraud Probability</span>
          <span className="text-lg font-bold text-gray-900 dark:text-white transition-colors duration-300">{formatScore(fraudAnalysis.overall_fraud_score)}%</span>
        </div>
        
        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3 mb-2 transition-colors duration-300">
          <div 
            className={`h-3 rounded-full transition-all duration-500 ${getProgressBarColor(fraudAnalysis.overall_fraud_score)}`}
            style={{ width: `${formatScore(fraudAnalysis.overall_fraud_score)}%` }}
          ></div>
        </div>
        
        <p className="text-sm text-gray-600 dark:text-gray-300 transition-colors duration-300">{fraudAnalysis.recommendation}</p>
      </div>

      {/* Risk Indicators */}
      {fraudAnalysis.indicators && fraudAnalysis.indicators.length > 0 && (
        <div className="mb-6">
          <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center transition-colors duration-300">
            <i className="fa-solid fa-triangle-exclamation text-yellow-500 dark:text-yellow-400 mr-2"></i>
            Risk Indicators ({fraudAnalysis.indicators.length})
          </h4>
          <div className="space-y-2">
            {fraudAnalysis.indicators.slice(0, 3).map((indicator, index) => (
              <div key={index} className="flex items-start p-3 bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-800 rounded-lg transition-colors duration-300">
                <i className="fa-solid fa-exclamation-triangle text-yellow-600 dark:text-yellow-400 mr-2 mt-0.5 text-sm transition-colors duration-300"></i>
                <span className="text-sm text-yellow-800 dark:text-yellow-200 transition-colors duration-300">{indicator}</span>
              </div>
            ))}
            {fraudAnalysis.indicators.length > 3 && (
              <div className="text-center">
                <button
                  onClick={() => setShowDetails(!showDetails)}
                  className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 font-medium transition-colors duration-200"
                >
                  {showDetails ? 'Show Less' : `Show ${fraudAnalysis.indicators.length - 3} More Indicators`}
                </button>
              </div>
            )}
          </div>
          
          {/* Additional indicators when expanded */}
          {showDetails && fraudAnalysis.indicators.length > 3 && (
            <div className="mt-3 space-y-2">
              {fraudAnalysis.indicators.slice(3).map((indicator, index) => (
                <div key={index + 3} className="flex items-start p-3 bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-800 rounded-lg transition-colors duration-300">
                  <i className="fa-solid fa-exclamation-triangle text-yellow-600 dark:text-yellow-400 mr-2 mt-0.5 text-sm transition-colors duration-300"></i>
                  <span className="text-sm text-yellow-800 dark:text-yellow-200 transition-colors duration-300">{indicator}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* No Indicators - All Good */}
      {(!fraudAnalysis.indicators || fraudAnalysis.indicators.length === 0) && (
        <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded-lg transition-colors duration-300">
          <div className="flex items-center">
            <i className="fa-solid fa-check-circle text-green-600 dark:text-green-400 mr-3 transition-colors duration-300"></i>
            <span className="text-sm text-green-800 dark:text-green-200 font-medium transition-colors duration-300">
              No suspicious indicators detected. Statement appears authentic.
            </span>
          </div>
        </div>
      )}

      {/* Summary Statistics */}
      {fraudAnalysis.summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="text-center p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg transition-colors duration-300">
            <div className="text-lg font-bold text-gray-800 dark:text-white transition-colors duration-300">{fraudAnalysis.summary.total_transactions}</div>
            <div className="text-xs text-gray-600 dark:text-gray-300 transition-colors duration-300">Transactions</div>
          </div>
          <div className="text-center p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg transition-colors duration-300">
            <div className="text-lg font-bold text-gray-800 dark:text-white transition-colors duration-300">{fraudAnalysis.summary.balance_errors || 0}</div>
            <div className="text-xs text-gray-600 dark:text-gray-300 transition-colors duration-300">Balance Errors</div>
          </div>
          <div className="text-center p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg transition-colors duration-300">
            <div className="text-lg font-bold text-gray-800 dark:text-white transition-colors duration-300">{(fraudAnalysis.summary.benford_p_value * 100).toFixed(1)}%</div>
            <div className="text-xs text-gray-600 dark:text-gray-300 transition-colors duration-300">Benford p-value</div>
          </div>
          <div className="text-center p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg transition-colors duration-300">
            <div className="text-lg font-bold text-gray-800 dark:text-white transition-colors duration-300">{fraudAnalysis.summary.pattern_flags || 0}</div>
            <div className="text-xs text-gray-600 dark:text-gray-300 transition-colors duration-300">Pattern Flags</div>
          </div>
        </div>
      )}

      {/* Detailed Analysis Toggle */}
      <div className="border-t border-gray-200 dark:border-gray-700 pt-4 transition-colors duration-300">
        <button
          onClick={() => setShowDetails(!showDetails)}
          className="w-full flex items-center justify-center text-sm text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-200 font-medium transition-colors duration-200"
        >
          <i className={`fa-solid ${showDetails ? 'fa-chevron-up' : 'fa-chevron-down'} mr-2`}></i>
          {showDetails ? 'Hide' : 'Show'} Detailed Analysis
        </button>
      </div>

      {/* Detailed Analysis */}
      {showDetails && fraudAnalysis.detailed_analysis && (
        <div className="mt-4 space-y-4">
          {/* Mathematical Validation */}
          {fraudAnalysis.detailed_analysis.mathematical_validation && (
            <div className="p-4 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg transition-colors duration-300">
              <h5 className="text-sm font-semibold text-blue-800 dark:text-blue-300 mb-3 transition-colors duration-300">
                <i className="fa-solid fa-calculator mr-2"></i>
                Mathematical Validation
              </h5>
              <div className="text-xs text-blue-700 dark:text-blue-300 space-y-2 transition-colors duration-300">
                <div className="flex justify-between">
                  <span>Balance Inconsistencies:</span>
                  <span className="font-semibold">{fraudAnalysis.detailed_analysis.mathematical_validation.inconsistencies_found || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span>Fraud Score:</span>
                  <span className="font-semibold">{(fraudAnalysis.detailed_analysis.mathematical_validation.fraud_score * 100).toFixed(1)}%</span>
                </div>
                
                {/* Detail Inconsistencies */}
                {fraudAnalysis.detailed_analysis.mathematical_validation.inconsistencies && 
                 fraudAnalysis.detailed_analysis.mathematical_validation.inconsistencies.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-blue-200 dark:border-blue-700">
                    <div className="font-semibold text-blue-800 dark:text-blue-300 mb-2 transition-colors duration-300">Detail Errors Found:</div>
                    <div className="space-y-2 max-h-32 overflow-y-auto">
                      {fraudAnalysis.detailed_analysis.mathematical_validation.inconsistencies.map((error, index) => (
                        <div key={index} className="p-2 bg-blue-100 dark:bg-blue-900/50 rounded border border-blue-300 dark:border-blue-700 transition-colors duration-300">
                          <div className="font-medium text-blue-900 dark:text-blue-200 text-xs transition-colors duration-300">
                            Transaction #{error.transaction_index + 1}
                          </div>
                          <div className="text-xs text-blue-800 dark:text-blue-300 mt-1 space-y-1 transition-colors duration-300">
                            <div>Expected Balance: <span className="font-medium">Rp {error.expected_balance?.toLocaleString('id-ID') || 'N/A'}</span></div>
                            <div>Actual Balance: <span className="font-medium">Rp {error.actual_balance?.toLocaleString('id-ID') || 'N/A'}</span></div>
                            <div>Difference: <span className="font-bold text-red-700 dark:text-red-400">Rp {Math.abs(error.difference || 0).toLocaleString('id-ID')}</span></div>
                            {error.transaction_date && (
                              <div>Date: <span className="font-medium">{error.transaction_date}</span></div>
                            )}
                            {error.description && (
                              <div>Description: <span className="text-blue-600 dark:text-blue-400">{error.description.substring(0, 50)}{error.description.length > 50 ? '...' : ''}</span></div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Benford's Law Analysis */}
          {fraudAnalysis.detailed_analysis.benfords_law && !fraudAnalysis.detailed_analysis.benfords_law.error && (
            <div className="p-4 bg-purple-50 dark:bg-purple-900/30 border border-purple-200 dark:border-purple-800 rounded-lg transition-colors duration-300">
              <h5 className="text-sm font-semibold text-purple-800 dark:text-purple-300 mb-2 transition-colors duration-300">Benford's Law Analysis</h5>
              <div className="text-xs text-purple-700 dark:text-purple-300 space-y-1 transition-colors duration-300">
                <div>Sample Size: {fraudAnalysis.detailed_analysis.benfords_law.sample_size}</div>
                <div>P-Value: {(fraudAnalysis.detailed_analysis.benfords_law.p_value * 100).toFixed(2)}%</div>
                <div>MAD: {fraudAnalysis.detailed_analysis.benfords_law.mean_absolute_deviation}</div>
                <div className="mt-2 text-purple-600 dark:text-purple-400 transition-colors duration-300">
                  {fraudAnalysis.detailed_analysis.benfords_law.interpretation}
                </div>
              </div>
            </div>
          )}

          {/* Pattern Analysis */}
          {fraudAnalysis.detailed_analysis.pattern_analysis && fraudAnalysis.detailed_analysis.pattern_analysis.length > 0 && (
            <div className="p-4 bg-orange-50 dark:bg-orange-900/30 border border-orange-200 dark:border-orange-800 rounded-lg transition-colors duration-300">
              <h5 className="text-sm font-semibold text-orange-800 dark:text-orange-300 mb-2 transition-colors duration-300">Pattern Analysis</h5>
              <div className="space-y-1">
                {fraudAnalysis.detailed_analysis.pattern_analysis.map((pattern, index) => (
                  <div key={index} className="text-xs text-orange-700 dark:text-orange-300 transition-colors duration-300">• {pattern}</div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Methodology Note */}
      <div className="mt-4 p-3 bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-lg transition-colors duration-300">
        <div className="flex items-start">
          <i className="fa-solid fa-info-circle text-gray-500 dark:text-gray-400 mr-2 mt-0.5 transition-colors duration-300"></i>
          <div className="text-xs text-gray-600 dark:text-gray-300 transition-colors duration-300">
            <strong>Analysis Methods:</strong> Mathematical balance validation, Benford's Law statistical analysis, 
            transaction pattern detection, and amount distribution analysis.
          </div>
        </div>
      </div>
    </div>
  );
};

export default FraudDetectionCard; 